import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '../../components/admin/DashboardLayout';
import { 
  Save, Lock, Globe, Mail, Phone, MapPin, 
  Instagram, Loader2, CheckCircle2 
} from 'lucide-react';
import { useNotification } from '../../context/NotificationContext';

const Settings = () => {
  const { addNotification } = useNotification();
  const [activeTab, setActiveTab] = useState('general'); // 'general' | 'security'
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // General Settings State
  const [settings, setSettings] = useState({
    whatsapp: '',
    email: '',
    address: '',
    maps_embed: '',
    instagram: '',
    tiktok: ''
  });

  // Password State
  const [passwordData, setPasswordData] = useState({
    new_password: '',
    confirm_password: ''
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL || 'https://konveksiimoonusel.com/api'}/admin/settings.php`);
      // Merge with defaults to avoid undefined errors
      setSettings(prev => ({ ...prev, ...res.data }));
    } catch (err) {
      addNotification('Gagal memuat pengaturan', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleGeneralSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL || 'https://konveksiimoonusel.com/api'}/admin/settings.php`, {
        settings: settings
      });
      addNotification('Pengaturan berhasil disimpan!', 'success');
    } catch (err) {
      addNotification('Gagal menyimpan pengaturan', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (passwordData.new_password !== passwordData.confirm_password) {
        addNotification('Password konfirmasi tidak cocok', 'error');
        return;
    }
    if (passwordData.new_password.length < 6) {
        addNotification('Password minimal 6 karakter', 'error');
        return;
    }

    setSaving(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL || 'https://konveksiimoonusel.com/api'}/admin/settings.php`, {
        action: 'change_password',
        new_password: passwordData.new_password
      });
      addNotification('Password admin berhasil diubah', 'success');
      setPasswordData({ new_password: '', confirm_password: '' });
    } catch (err) {
      addNotification('Gagal mengubah password', 'error');
    } finally {
      setSaving(false);
    }
  };

  const styles = {
    header: { marginBottom: '2rem' },
    title: { fontSize: '1.5rem', fontWeight: '700', color: '#111' },
    subtitle: { color: '#6b7280', marginTop: '0.25rem' },
    
    tabs: { display: 'flex', gap: '1rem', borderBottom: '1px solid #e5e7eb', marginBottom: '2rem' },
    tabBtn: (active) => ({
      padding: '0.75rem 1rem',
      borderBottom: active ? '2px solid #111' : '2px solid transparent',
      color: active ? '#111' : '#6b7280',
      fontWeight: active ? '600' : '500',
      cursor: 'pointer',
      background: 'none', borderTop: 'none', borderLeft: 'none', borderRight: 'none',
      fontSize: '0.95rem',
      display: 'flex', alignItems: 'center', gap: '0.5rem'
    }),
    
    card: { backgroundColor: '#fff', padding: '2rem', borderRadius: '1rem', border: '1px solid #e5e7eb', maxWidth: '800px' },
    formGroup: { marginBottom: '1.5rem' },
    label: { display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.9rem', color: '#374151' },
    inputWrapper: { position: 'relative', display: 'flex', alignItems: 'center' },
    icon: { position: 'absolute', left: '1rem', color: '#9ca3af', pointerEvents: 'none' },
    input: { 
      width: '100%', padding: '0.75rem 1rem 0.75rem 3rem', 
      border: '1px solid #d1d5db', borderRadius: '0.5rem', fontSize: '0.95rem',
      outline: 'none', transition: 'border-color 0.2s'
    },
    textarea: {
      width: '100%', padding: '0.75rem 1rem', 
      border: '1px solid #d1d5db', borderRadius: '0.5rem', fontSize: '0.95rem',
      outline: 'none', minHeight: '100px', resize: 'vertical'
    },
    helper: { fontSize: '0.8rem', color: '#6b7280', marginTop: '0.25rem' },
    
    saveBtn: {
      backgroundColor: '#111', color: '#fff',
      padding: '0.75rem 2rem', borderRadius: '0.5rem',
      border: 'none', fontWeight: '600', cursor: 'pointer',
      display: 'flex', alignItems: 'center', gap: '0.5rem',
      opacity: saving ? 0.7 : 1,
      marginTop: '1rem'
    }
  };

  return (
    <DashboardLayout>
      <div style={styles.header}>
        <h1 style={styles.title}>Pengaturan Website</h1>
        <p style={styles.subtitle}>Kelola informasi kontak, sosial media, dan keamanan akun.</p>
      </div>

      <div style={styles.tabs}>
        <button style={styles.tabBtn(activeTab === 'general')} onClick={() => setActiveTab('general')}>
          <Globe size={18} /> Informasi Umum
        </button>
        <button style={styles.tabBtn(activeTab === 'security')} onClick={() => setActiveTab('security')}>
          <Lock size={18} /> Keamanan Akun
        </button>
      </div>

      {loading ? (
        <div>Memuat pengaturan...</div>
      ) : (
        <>
          {/* TAB: GENERAL SETTINGS */}
          {activeTab === 'general' && (
            <div style={styles.card}>
              <form onSubmit={handleGeneralSubmit}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Nomor WhatsApp Utama</label>
                  <div style={styles.inputWrapper}>
                    <Phone size={18} style={styles.icon} />
                    <input 
                      type="text" 
                      style={styles.input} 
                      value={settings.whatsapp}
                      onChange={e => setSettings({...settings, whatsapp: e.target.value})}
                      placeholder="628..."
                    />
                  </div>
                  <p style={styles.helper}>Gunakan format internasional (628...). Ini akan jadi tujuan tombol WA di website.</p>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Email Resmi</label>
                  <div style={styles.inputWrapper}>
                    <Mail size={18} style={styles.icon} />
                    <input 
                      type="email" 
                      style={styles.input} 
                      value={settings.email}
                      onChange={e => setSettings({...settings, email: e.target.value})}
                      placeholder="admin@imoonusel.com"
                    />
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Alamat Lengkap</label>
                  <div style={styles.inputWrapper}>
                    <MapPin size={18} style={{...styles.icon, top: '1rem'}} />
                    <textarea 
                      style={{...styles.textarea, paddingLeft: '3rem'}} 
                      value={settings.address}
                      onChange={e => setSettings({...settings, address: e.target.value})}
                      placeholder="Jl. Contoh No. 123..."
                    />
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Google Maps Embed Code (Iframe)</label>
                  <div style={styles.inputWrapper}>
                    <Globe size={18} style={{...styles.icon, top: '1rem'}} />
                    <textarea 
                      style={{...styles.textarea, paddingLeft: '3rem', minHeight: '80px', fontFamily: 'monospace', fontSize: '0.8rem'}} 
                      value={settings.maps_embed}
                      onChange={e => setSettings({...settings, maps_embed: e.target.value})}
                      placeholder='Paste kode iframe Google Maps DI SINI, atau ketik alamat/koordinat langsung (misal: "Girimukti, Batujajar").'
                    />
                  </div>
                  <p style={styles.helper}>Bisa isi dengan kode <b>&lt;iframe&gt;</b> dari Google Maps, atau cukup tulis alamat lengkap/titik koordinat.</p>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Link Instagram</label>
                  <div style={styles.inputWrapper}>
                    <Instagram size={18} style={styles.icon} />
                    <input 
                      type="text" 
                      style={styles.input} 
                      value={settings.instagram}
                      onChange={e => setSettings({...settings, instagram: e.target.value})}
                      placeholder="https://instagram.com/..."
                    />
                  </div>
                </div>

                <button type="submit" style={styles.saveBtn} disabled={saving}>
                  {saving ? <Loader2 className="animate-spin" size={20}/> : <Save size={20}/>}
                  {saving ? 'Menyimpan...' : 'Simpan Perubahan'}
                </button>
              </form>
            </div>
          )}

          {/* TAB: SECURITY SETTINGS */}
          {activeTab === 'security' && (
            <div style={styles.card}>
              <div style={{marginBottom: '2rem', padding: '1rem', backgroundColor: '#eff6ff', borderRadius: '0.5rem', border: '1px solid #dbeafe', color: '#1e40af', fontSize: '0.9rem'}}>
                <strong>Info Keamanan:</strong> Gunakan password yang kuat (kombinasi huruf & angka). Jangan berikan akses ini ke sembarang orang.
              </div>
              
              <form onSubmit={handlePasswordSubmit}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Password Baru</label>
                  <div style={styles.inputWrapper}>
                    <Lock size={18} style={styles.icon} />
                    <input 
                      type="password" 
                      style={styles.input} 
                      value={passwordData.new_password}
                      onChange={e => setPasswordData({...passwordData, new_password: e.target.value})}
                      placeholder="Masukkan password baru"
                      required
                    />
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Konfirmasi Password Baru</label>
                  <div style={styles.inputWrapper}>
                    <CheckCircle2 size={18} style={styles.icon} />
                    <input 
                      type="password" 
                      style={styles.input} 
                      value={passwordData.confirm_password}
                      onChange={e => setPasswordData({...passwordData, confirm_password: e.target.value})}
                      placeholder="Ulangi password baru"
                      required
                    />
                  </div>
                </div>

                <button type="submit" style={styles.saveBtn} disabled={saving}>
                  {saving ? <Loader2 className="animate-spin" size={20}/> : <Save size={20}/>}
                  {saving ? 'Memproses...' : 'Ganti Password'}
                </button>
              </form>
            </div>
          )}
        </>
      )}
    </DashboardLayout>
  );
};

export default Settings;
